import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../../redux/store";
import { openModal, VALID_MODAL_TYPES, type ModalType } from "../../redux/slices/uiSlice";
import LoginModal from "./LoginModal";
import OtpVerifyModal from "./OtpVerifyModal";
import ForgotPasswordModal from "./ForgotPasswordModal";
import ResetLinkSentModal from "./ResetLinkSentModal";
import ResetPasswordModal from "./ResetPasswordModal";
import RegisterModal from "./RegisterModal";
import VerifyAccountModal from "./VerifyAccount";
import VerifyEmailModal from "./VerifyEmailModal";
import ContactUsModal from "./ContactUsModal";
import PlayInfo from "../Modal/PlayInfo";
import CategorySuggestionModal from "../Modal/CategorySuggestionModal";
import SupportModal from "../Modal/SupportModal";
import DeleteAccountModal from "../Modal/myaccountmodal/DeleteAccountModal";

const GlobalModal: React.FC = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { isOpen, modalType } = useSelector((state: RootState) => state.ui.modal);
  const emailVerification = useSelector((state: RootState) => state.auth.emailVerification);

  // Sync modal state from URL params on mount
  useEffect(() => {
    const isOpenParam = searchParams.get("isOpen") === "true";
    const modalTypeParam = searchParams.get("modaltype");
    const validModalType =
      modalTypeParam && VALID_MODAL_TYPES.includes(modalTypeParam as ModalType)
        ? (modalTypeParam as ModalType)
        : null;

    if (isOpenParam && validModalType) {
      dispatch(openModal(validModalType));
    }
  }, [searchParams, dispatch]);

  // Disable body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Global reCAPTCHA container - persists across modal switches
  // This prevents "reCAPTCHA has already been rendered" errors
  return (
    <>
      {/* Global reCAPTCHA container - hidden but persistent */}
      <div id="global-recaptcha-container" className="hidden" aria-hidden="true" />

      {isOpen && modalType && renderModal(modalType, emailVerification)}
    </>
  );
};

function renderModal(modalType: string, emailVerification?: string | null) {
  switch (modalType) {
    case "login":
      return <LoginModal />;
    case "register":
      return <RegisterModal />;
    case "verifyAccount":
      return <VerifyAccountModal />;
    case "verifyEmail":
      return <VerifyEmailModal email={emailVerification || undefined} />;
    case "otpVerify":
      return <OtpVerifyModal />;
    case "forgotPassword":
      return <ForgotPasswordModal />;
    case "resetLinkSent":
      return <ResetLinkSentModal />;
    case "resetPassword":
      return <ResetPasswordModal />;
    case "contactUs":
      return <ContactUsModal />;
    case "playInfo":
      return <PlayInfo />;
    case "categorySuggestion":
      return <CategorySuggestionModal />;
    case "support":
      return <SupportModal />;
    case "deleteAccount":
      return <DeleteAccountModal />;
    default:
      return null;
  }
}

export default GlobalModal;
